/**
 * The planner tool registry: every tool is a Gemini FunctionDeclaration
 * paired with a JS executor that reads the live wedding snapshot or calls an
 * existing Firestore operations helper.
 */

import type { FunctionDeclaration } from "firebase/ai";
import type { ToolContext } from "../types";
import { readTools } from "./readTools";
import { guestTools } from "./guestTools";
import { taskTools } from "./taskTools";
import { budgetTools } from "./budgetTools";
import { dayPlanTools } from "./dayPlanTools";
import { searchTools } from "./searchTools";
import { seatingTools } from "./seatingTools";
import { questionnaireTools } from "./questionnaireTools";
import { skillTools } from "./skillTools";
import { memoryTools } from "./memoryTools";
import { websiteTools } from "./websiteTools";
import { rsvpTools } from "./rsvpTools";

export interface PlannerTool {
  declaration: FunctionDeclaration;
  /** Destructive tools require explicit user confirmation before executing. */
  destructive: boolean;
  /** Interactive tools pause the loop until the user submits a form. */
  interactive?: boolean;
  /**
   * Runs the tool. Returns a plain, JSON-serialisable object handed back to
   * the model as the functionResponse payload. May throw — the agent loop
   * catches and reports failures to the model so it can recover.
   */
  execute: (args: Record<string, unknown>, ctx: ToolContext) => Promise<Record<string, unknown>>;
  /** Short localised label for the activity log / confirmation prompt. */
  describe: (args: Record<string, unknown>, ctx: ToolContext) => string;
}

export const PLANNER_TOOLS: PlannerTool[] = [
  ...readTools,
  ...searchTools,
  ...skillTools,
  ...guestTools,
  ...taskTools,
  ...budgetTools,
  ...dayPlanTools,
  ...questionnaireTools,
  ...seatingTools,
  ...memoryTools,
  ...websiteTools,
  ...rsvpTools,
];

const TOOL_BY_NAME = new Map<string, PlannerTool>(
  PLANNER_TOOLS.map((t) => [t.declaration.name, t]),
);

export function findTool(name: string): PlannerTool | undefined {
  return TOOL_BY_NAME.get(name);
}

export function getToolDeclarations(): FunctionDeclaration[] {
  return PLANNER_TOOLS.map((t) => t.declaration);
}
